import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { UserGroupIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const ProgramAssignment = () => {
  const { user } = useAuth();
  const token = user?.token || localStorage.getItem('token');
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
  const [searchParams] = useSearchParams();
  const programId = searchParams.get('programId');

  const [activeTab, setActiveTab] = useState('assignment'); // 'assignment' | 'rollforward'
  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState(null); // { program, tryouts, registrants, teams, ageGroups }

  // Assignment state: per-registrant target choice ("team:ID" or "ag:ID")
  const [targets, setTargets] = useState({});
  const [ageGroupFilter, setAgeGroupFilter] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Roll forward state
  const [sourceTeamId, setSourceTeamId] = useState('');
  const [targetTeamId, setTargetTeamId] = useState('');
  const [sourcePlayers, setSourcePlayers] = useState([]);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState(new Set());
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [rolling, setRolling] = useState(false);

  const fetchContext = useCallback(async () => {
    if (!token || !programId) return;
    try {
      setLoading(true);
      const response = await axios.get(`/api/programs/${programId}/assignment-context`, { headers: authHeaders });
      setContext(response.data);
    } catch (error) {
      console.error('Error loading program assignment context:', error);
      toast.error(error.response?.data?.message || 'Failed to load program');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, programId]);

  useEffect(() => {
    fetchContext();
  }, [fetchContext]);

  const teams = context?.teams || [];
  const ageGroups = context?.ageGroups || [];
  const registrants = context?.registrants || [];

  const filteredRegistrants = useMemo(() => {
    if (!ageGroupFilter) return registrants;
    return registrants.filter(r => {
      if (!r.assignedAgeGroup) return false;
      return String(r.assignedAgeGroup.id) === String(ageGroupFilter)
        || (r.assignedAgeGroup.name || '').toLowerCase() === ageGroupFilter.toLowerCase();
    });
  }, [registrants, ageGroupFilter]);

  const setTarget = (registrantId, value) => {
    setTargets(prev => ({ ...prev, [registrantId]: value }));
  };

  const assignOne = async (registrant) => {
    const value = targets[registrant.registrantId];
    if (!value) {
      toast.error('Choose a team or age group first.');
      return;
    }
    const [kind, id] = value.split(':');
    const assignment = { registrantId: registrant.registrantId };
    if (kind === 'team') assignment.teamId = parseInt(id, 10);
    else if (kind === 'ag') assignment.ageGroupId = parseInt(id, 10);

    try {
      setAssigning(true);
      const response = await axios.post(`/api/programs/${programId}/assign`, { assignments: [assignment] }, { headers: authHeaders });
      const result = response.data?.results?.[0];
      if (result?.status === 'assigned') {
        toast.success(`Assigned ${registrant.user?.name || 'player'}${result.programInvoice ? ` (invoice ${result.programInvoice.status})` : ''}`);
        await fetchContext();
      } else {
        toast.error(result?.reason || 'Assignment failed');
      }
    } catch (error) {
      console.error('Error assigning registrant:', error);
      toast.error(error.response?.data?.message || 'Assignment failed');
    } finally {
      setAssigning(false);
    }
  };

  const loadSourcePlayers = useCallback(async (teamId) => {
    if (!teamId) {
      setSourcePlayers([]);
      return;
    }
    try {
      setLoadingPlayers(true);
      const response = await axios.get(`/api/roster?teamId=${teamId}`, { headers: authHeaders });
      const players = (Array.isArray(response.data) ? response.data : []).filter(m => m.role === 'player');
      setSourcePlayers(players);
      setSelectedPlayerIds(new Set());
    } catch (error) {
      console.error('Error loading source team players:', error);
      toast.error(error.response?.data?.error || 'Failed to load team players');
      setSourcePlayers([]);
    } finally {
      setLoadingPlayers(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    loadSourcePlayers(sourceTeamId);
  }, [sourceTeamId, loadSourcePlayers]);

  const togglePlayer = (id) => {
    setSelectedPlayerIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const submitRollForward = async () => {
    if (!targetTeamId) {
      toast.error('Select a target team.');
      return;
    }
    if (selectedPlayerIds.size === 0) {
      toast.error('Select at least one player to roll forward.');
      return;
    }
    if (sourceTeamId && targetTeamId && sourceTeamId === targetTeamId) {
      toast.error('Source and target teams must be different.');
      return;
    }
    try {
      setRolling(true);
      const response = await axios.post(`/api/programs/${programId}/roll-forward`, {
        sourceTeamId: sourceTeamId ? parseInt(sourceTeamId, 10) : null,
        targetTeamId: parseInt(targetTeamId, 10),
        playerUserIds: Array.from(selectedPlayerIds)
      }, { headers: authHeaders });
      const count = response.data?.rolledForward || 0;
      toast.success(`Rolled forward ${count} player${count === 1 ? '' : 's'}`);
      setSelectedPlayerIds(new Set());
      await fetchContext();
    } catch (error) {
      console.error('Error rolling forward players:', error);
      toast.error(error.response?.data?.message || 'Failed to roll forward players');
    } finally {
      setRolling(false);
    }
  };

  if (!programId) {
    return <div className="max-w-4xl mx-auto py-12 text-center text-gray-500">No program selected.</div>;
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {context?.program?.name ? `${context.program.name} - Assignment` : 'Program Assignment'}
        </h1>
        {context?.program && (
          <p className="text-sm text-gray-500">
            Season start {context.program.start_date} (drives player age){context.program.end_date ? ` - ${context.program.end_date}` : ''}
          </p>
        )}
      </div>

      <div className="flex gap-2 border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('assignment')}
          className={`px-4 py-2 text-sm font-medium border-b-2 ${activeTab === 'assignment' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          <UserGroupIcon className="h-4 w-4 inline mr-1" /> Assignment
        </button>
        <button
          onClick={() => setActiveTab('rollforward')}
          className={`px-4 py-2 text-sm font-medium border-b-2 ${activeTab === 'rollforward' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          <ArrowPathIcon className="h-4 w-4 inline mr-1" /> Roll Forward
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : activeTab === 'assignment' ? (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Filter by age group</label>
              <select
                value={ageGroupFilter}
                onChange={(e) => setAgeGroupFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">All age groups</option>
                {ageGroups.map(ag => <option key={ag.id} value={ag.id}>{ag.name}</option>)}
              </select>
            </div>
            <span className="text-sm text-gray-500">{filteredRegistrants.length} unassigned registrant{filteredRegistrants.length === 1 ? '' : 's'}</span>
          </div>

          {filteredRegistrants.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">No unassigned registrants.</div>
          ) : (
            <div className="bg-white rounded-lg shadow divide-y divide-gray-100">
              {filteredRegistrants.map(reg => (
                <div key={reg.registrantId} className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{reg.user?.name || 'Unknown player'}</div>
                    <div className="text-xs text-gray-500">
                      {reg.assignedAgeGroup ? `Age group: ${reg.assignedAgeGroup.name}` : 'Age group: unknown'}
                      {reg.eventTitle ? ` - from ${reg.eventTitle}` : ''}
                      {reg.isGuest ? ' - guest' : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={targets[reg.registrantId] || ''}
                      onChange={(e) => setTarget(reg.registrantId, e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="">Assign to...</option>
                      <optgroup label="Teams">
                        {teams.map(t => (
                          <option key={`team-${t.id}`} value={`team:${t.id}`}>
                            {t.name}{t.season ? ` (${t.season})` : ''}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Age groups">
                        {ageGroups.map(ag => (
                          <option key={`ag-${ag.id}`} value={`ag:${ag.id}`}>{ag.name}</option>
                        ))}
                      </optgroup>
                    </select>
                    <button
                      onClick={() => assignOne(reg)}
                      disabled={assigning}
                      className="px-3 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-50"
                    >
                      Assign
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-gray-400 mt-3">
            Assigning to a team adds the player to the roster and auto-invoices the program fee. Assigning to an
            age group groups the player without placing them on a specific team yet.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6 space-y-5">
          <p className="text-sm text-gray-600">
            Roll forward keeps players on their current team while also adding them to the new team and
            auto-invoicing the program fee.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source team (current)</label>
              <select
                value={sourceTeamId}
                onChange={(e) => setSourceTeamId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Select source team</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.name}{t.season ? ` (${t.season})` : ''}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target team (new)</label>
              <select
                value={targetTeamId}
                onChange={(e) => setTargetTeamId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Select target team</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.name}{t.season ? ` (${t.season})` : ''}</option>)}
              </select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-800">Players on source team</h3>
              {sourcePlayers.length > 0 && (
                <button
                  onClick={() => setSelectedPlayerIds(
                    selectedPlayerIds.size === sourcePlayers.length
                      ? new Set()
                      : new Set(sourcePlayers.map(p => p.id))
                  )}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  {selectedPlayerIds.size === sourcePlayers.length ? 'Deselect all' : 'Select all'}
                </button>
              )}
            </div>
            {loadingPlayers ? (
              <div className="text-sm text-gray-500 py-4">Loading players...</div>
            ) : !sourceTeamId ? (
              <div className="text-sm text-gray-500 py-4">Select a source team to list its players.</div>
            ) : sourcePlayers.length === 0 ? (
              <div className="text-sm text-gray-500 py-4">No players on this team.</div>
            ) : (
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-96 overflow-y-auto">
                {sourcePlayers.map(p => (
                  <label key={p.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedPlayerIds.has(p.id)}
                      onChange={() => togglePlayer(p.id)}
                      className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-900">{p.name}</span>
                    {p.status === 'pending' && <span className="text-xs text-amber-600">pending</span>}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <button
              onClick={submitRollForward}
              disabled={rolling || selectedPlayerIds.size === 0 || !targetTeamId}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {rolling ? 'Rolling forward...' : `Roll forward ${selectedPlayerIds.size || ''} player${selectedPlayerIds.size === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgramAssignment;
